import React, { useState, useEffect, useRef } from 'react';
import styles from './ScrollToBottomButton.module.css';
import { IoIosArrowDown } from 'react-icons/io';

interface ScrollToBottomButtonProps {
  containerRef: React.RefObject<HTMLElement>;
  threshold?: number; // Distance from bottom in pixels to show the button
  isNewMessageArriving?: boolean; // Flag to indicate if a new message is arriving
}

const ScrollToBottomButton: React.FC<ScrollToBottomButtonProps> = ({ 
  containerRef,
  threshold = 100,
  isNewMessageArriving = false
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastScrollPositionRef = useRef<number>(0);

  // Function to check scroll position with debounce
  const checkScrollPosition = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Don't show the button if a new message is arriving
    if (isNewMessageArriving) {
      setIsVisible(false);
      return;
    }

    debounceTimerRef.current = setTimeout(() => {
      const container = containerRef.current;
      if (!container) return;

      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      
      // Only update visibility if the scroll position has changed significantly
      // or if we're transitioning from visible to not visible
      const scrollPositionChanged = Math.abs(lastScrollPositionRef.current - scrollTop) > 5;
      
      if (scrollPositionChanged || (isVisible && distanceFromBottom <= threshold)) {
        lastScrollPositionRef.current = scrollTop;
        setIsVisible(distanceFromBottom > threshold);
      }
    }, 150); // Debounce time
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Initial check
    checkScrollPosition();

    // Add scroll event listener
    container.addEventListener('scroll', checkScrollPosition);

    // Clean up
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      container.removeEventListener('scroll', checkScrollPosition);
    };
  }, [containerRef, threshold, isNewMessageArriving]);

  // Effect to handle isNewMessageArriving changes
  useEffect(() => {
    if (isNewMessageArriving) {
      setIsVisible(false);
    } else {
      // Small delay before checking scroll position after a message arrives
      // to ensure the DOM has updated
      const timer = setTimeout(() => {
        checkScrollPosition();
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [isNewMessageArriving]);

  const scrollToBottom = () => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: 'smooth'
      });
      
      // Hide the button immediately after clicking
      setIsVisible(false);
    }
  };

  if (!isVisible) return null;

  return (
    <button 
      className={styles.scrollToBottomButton}
      onClick={scrollToBottom}
      aria-label="Scroll to bottom"
    >
      <IoIosArrowDown size={24} />
    </button>
  );
};

export default ScrollToBottomButton; 